import dayjs from 'dayjs';
import { serviceErrorCodes } from '~/constants';
import type { SdkError } from '~/types/interaction';
import type { LoanRecord } from '~/types/loans';
import { journeyStatus } from '~/types/loans';
import { dateFormat } from './constants';

export const getCookie = (cookieName: string) => {
    const name = `${cookieName}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
};

export const isDateRangeWithinLimit = (
    startDate: string,
    endDate: string,
    range: number,
) => {
    const fromDate = dayjs(startDate, dateFormat);
    const toDate = dayjs(endDate, dateFormat);
    const diffInMonths = toDate.diff(fromDate, 'month', true);
    return diffInMonths <= range;
};

export const getErrorMessage = (e: SdkError) => {
    if (!e.code) {
        if (e.message === 'Invalid referrer') {
            return 'Uh-ho, the link for loan application is invalid. Please check';
        }
        return 'Facing any issue? Please try again later or contact us';
    }
    switch (e.code) {
        case serviceErrorCodes.USER_CANCELLED:
            return 'Please refresh the page to try again!';
        case serviceErrorCodes.NO_ACTIVE_LOAN:
            return 'Uh-ho, no active loan found to open the dashboard';
        case serviceErrorCodes.APPLICATION_NOT_APPROVED:
            return 'Uh-ho, the application is not approved';
        default:
            return 'Facing any issue? Please try again later or contact us';
    }
};

// Escape CSV values (handle commas, quotes, newlines)
export const escapeCSV = (value: string): string => {
    if (value === null || value === undefined) {
        return '';
    }
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
    ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

// Type for loan with API field variations
type LoanWithVariations = LoanRecord & {
    type?: string;
    MaxAmount?: string;
    ConfirmedAmount?: string;
    createdAt?: string;
    updatedAt?: string;
};

// Convert loan data to CSV format
export const convertLoansToCSV = (loanData: LoanRecord[]): string => {
    if (loanData.length === 0) {
        return '';
    }

    // Define CSV headers based on table columns (excluding Action column)
    const headers = [
        'Loan No(VLAN)',
        'Phone Number',
        'Customer Name',
        'Current Status',
        'Email Id',
        'PAN',
        'Loan Type',
        'Eligible Loan Amount',
        'Loan Amount Confirmed',
        'Loan Started At',
        'Loan Modified At',
        'Partner',
        'Referrer Id',
    ];

    // Convert data rows
    const rows = loanData.map((loan) => {
        // Handle field name variations (API might return different field names)
        const loanWithVariations = loan as LoanWithVariations;
        const loanType = loanWithVariations.type || loan.loanType || '';
        const eligibleAmount =
            loanWithVariations.MaxAmount || loan.eligibleLoanAmount || '';
        const confirmedAmount =
            loanWithVariations.ConfirmedAmount ||
            loan.loanAmountConfirmed ||
            '';
        const createdAt =
            loanWithVariations.createdAt || loan.loanStartedAt || '';
        const updatedAt =
            loanWithVariations.updatedAt || loan.loanModifiedAt || '';

        // Format dates
        const formattedCreatedAt = createdAt
            ? dayjs(createdAt).format('YYYY-MM-DD HH:mm')
            : '';
        const formattedUpdatedAt = updatedAt
            ? dayjs(updatedAt).format('YYYY-MM-DD HH:mm')
            : '';

        // Map journey status
        const statusLabel =
            journeyStatus[loan.journeyStatus as keyof typeof journeyStatus] ||
            loan.journeyStatus;

        return [
            loan.loanApplicationNumber || '-',
            loan.phone || '-',
            loan.customerName || '',
            statusLabel || '',
            loan.userEmail || '',
            loan.pan || '',
            loanType || '',
            eligibleAmount || '',
            confirmedAmount || '',
            formattedCreatedAt || '',
            formattedUpdatedAt || '',
            loan.partner || '',
            loan.referrerId || '',
        ];
    });

    // Combine headers and rows
    const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    return csvContent;
};
